/**
 * Keeps the promo gift line in sync with the cart.
 *
 * The Shopify automatic "Buy X get Y" discount handles the money side, but it
 * never puts the gift in the cart on its own — the customer has to already have
 * it there. This adds the gift whenever a qualifying product is in the cart,
 * matches its quantity, and takes it back out when the qualifier goes away.
 *
 * Config comes from window.freeGiftPromo, rendered in layout/theme.liquid.
 */
(function () {
  const config = window.freeGiftPromo;
  if (!config || !config.enabled || !config.giftVariantId) return;
  if (!config.triggerVariantIds || !config.triggerVariantIds.length) return;

  const PROPERTY = "_free_gift";
  const DISABLED_KEY = "freeGiftPromoOff";
  const cartRoutes = window.routes || {};
  const maxGifts = config.maxGifts || 10;

  let syncing = false;
  let syncAgain = false;

  // If the gift ever lands in the cart at full price the backing discount is
  // gone, so stop gifting for the rest of the session rather than quietly
  // charging people for it.
  function promoIsOff() {
    try {
      return sessionStorage.getItem(DISABLED_KEY) === "1";
    } catch (e) {
      return false;
    }
  }

  function turnPromoOff() {
    try {
      sessionStorage.setItem(DISABLED_KEY, "1");
    } catch (e) {
      /* private mode — fall through, we still removed the line */
    }
  }

  function postConfig() {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    };
  }

  function getCart() {
    return fetch(`${cartRoutes.cart_url}.js`, {
      headers: { Accept: "application/json" },
    }).then((response) => response.json());
  }

  // Splits the cart into the parts the promo cares about: how many qualifying
  // items are present, the gift line we added, and any copies of the gift the
  // customer added themselves — those already earn the discount, so they count
  // against what we owe them.
  function readPromoState(cart) {
    let triggerQuantity = 0;
    let giftLine = null;
    let customerAddedGifts = 0;

    cart.items.forEach((item) => {
      if (config.triggerVariantIds.indexOf(item.variant_id) !== -1) {
        triggerQuantity += item.quantity;
      }
      if (item.variant_id === config.giftVariantId) {
        if (item.properties && item.properties[PROPERTY]) {
          giftLine = item;
        } else {
          customerAddedGifts += item.quantity;
        }
      }
    });

    return { triggerQuantity, giftLine, customerAddedGifts };
  }

  function giftQuantityFor(state) {
    if (state.triggerQuantity === 0) return 0;
    const earned = Math.min(state.triggerQuantity, maxGifts);
    return Math.max(0, earned - state.customerAddedGifts);
  }

  function setGiftQuantity(giftLine, quantity) {
    if (giftLine) {
      return fetch(`${cartRoutes.cart_change_url}.js`, {
        ...postConfig(),
        body: JSON.stringify({ id: giftLine.key, quantity: quantity }),
      });
    }

    const properties = {};
    properties[PROPERTY] = config.label || "Free gift";

    return fetch(`${cartRoutes.cart_add_url}.js`, {
      ...postConfig(),
      body: JSON.stringify({
        items: [
          {
            id: config.giftVariantId,
            quantity: quantity,
            properties: properties,
          },
        ],
      }),
    });
  }

  // Confirms the discount actually zeroed the gift we just added. If it didn't,
  // pull the line back out and stand down for the session.
  function verifyGiftIsFree() {
    return getCart().then((cart) => {
      const state = readPromoState(cart);
      if (!state.giftLine || state.giftLine.final_line_price === 0) {
        return { itemCount: cart.item_count };
      }

      turnPromoOff();
      return fetch(`${cartRoutes.cart_change_url}.js`, {
        ...postConfig(),
        body: JSON.stringify({ id: state.giftLine.key, quantity: 0 }),
      })
        .then((response) => response.json())
        .then((updated) => ({ itemCount: updated.item_count }));
    });
  }

  // Resolves to { itemCount } when the cart changed, or null when it was
  // already correct and there is nothing to re-render.
  function sync() {
    if (promoIsOff()) return Promise.resolve(null);

    if (syncing) {
      syncAgain = true;
      return Promise.resolve(null);
    }
    syncing = true;

    return getCart()
      .then((cart) => {
        const state = readPromoState(cart);
        const wanted = giftQuantityFor(state);
        const current = state.giftLine ? state.giftLine.quantity : 0;
        if (wanted === current) return null;

        return setGiftQuantity(state.giftLine, wanted).then((response) => {
          // Sold out or otherwise rejected — leave the cart as the customer
          // built it rather than surfacing an error they cannot act on.
          if (!response.ok) return null;
          if (wanted === 0) {
            return response
              .json()
              .then((updated) => ({ itemCount: updated.item_count }));
          }
          return verifyGiftIsFree();
        });
      })
      .catch((error) => {
        console.error("free-gift:", error);
        return null;
      })
      .finally(() => {
        syncing = false;
      })
      .then((result) => {
        if (!syncAgain) return result;
        syncAgain = false;
        return sync().then((later) => later || result);
      });
  }

  function applyEmptyState(itemCount) {
    const isEmpty = itemCount === 0;
    [
      document.querySelector("cart-items"),
      document.querySelector("cart-drawer-items"),
      document.getElementById("main-cart-footer"),
      document.querySelector("cart-drawer"),
    ].forEach((element) => {
      if (element) element.classList.toggle("is-empty", isEmpty);
    });
  }

  // The cart page renders its own sections; the drawer has a refresh listener.
  function refreshCartPageSections() {
    const sections = [];
    const items = document.getElementById("main-cart-items");
    const footer = document.getElementById("main-cart-footer");

    if (items) {
      sections.push({
        id: "main-cart-items",
        section: items.dataset.id,
        selector: ".js-contents",
      });
    }
    if (footer) {
      sections.push({
        id: "main-cart-footer",
        section: footer.dataset.id,
        selector: ".js-contents",
      });
    }
    if (!sections.length) return;

    if (document.getElementById("cart-icon-bubble")) {
      sections.push({
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      });
    }

    fetch(
      `${cartRoutes.cart_url}?sections=${sections.map((section) => section.section).join(",")}`,
    )
      .then((response) => response.json())
      .then((data) => {
        sections.forEach((section) => {
          const container = document.getElementById(section.id);
          const html = data[section.section];
          if (!container || !html) return;

          const source = new DOMParser()
            .parseFromString(html, "text/html")
            .querySelector(section.selector);
          const target = container.querySelector(section.selector) || container;
          if (source) target.innerHTML = source.innerHTML;
        });
      })
      .catch((error) => console.error("free-gift:", error));
  }

  function refreshCart(itemCount) {
    applyEmptyState(itemCount);
    document.dispatchEvent(
      new CustomEvent("cart-drawer:refresh", { bubbles: true }),
    );
    refreshCartPageSections();
  }

  function syncAndRender() {
    sync().then((result) => {
      if (result) refreshCart(result.itemCount);
    });
  }

  if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
    subscribe(PUB_SUB_EVENTS.cartUpdate, syncAndRender);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAndRender);
  } else {
    syncAndRender();
  }
})();
